import React, { useState, useEffect, useMemo, useRef } from 'react';
import { pastels, fmt, clamp, abbr } from '@/util';
import { Button } from '@teo/components';
import {
  ArrowLeft,
  MoreVertical,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
} from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { SplitIcon } from '@/components/Froala/widgets/ChatGPT/Icon/index';

const colorFor = (i) => pastels[i % pastels.length];

const CLICK_TOL = 3; // px
const SNAP = 0.1; // seconds
const MIN_SPAN = 0.2; // seconds minimum segment length
const GAP_PX = 1; // already there
const HANDLE_W = Math.max(8, GAP_PX + 4);
const SELECT_PAD = 2; // px whitespace between selection border and the colored fill

const EPS = 0.05; // 50 ms tolerance so edge clicks still count as "inside"
const MAX_TICKS = 10; // max number of ruler labels we want

/** Pick a timestamp just inside [start,end] so we don't land on the boundary. */
const safeInside = (start, end) => {
  const pad = Math.max(0.05, SNAP); // >= 50ms
  let t = start + pad;
  if (!Number.isFinite(t) || t >= end) t = (start + end) / 2;
  // clamp with tiny epsilon margins
  return clamp(t, start + 1e-3, Math.max(start + 1e-3, end - 1e-3));
};

const ClickDropdownWrapper = ({
  i,
  addBefore,
  addAfter,
  splitStep,
  removeStep,
  newActive,
  p,
  prevP,
  nextP,
  durationSec,
  setActiveStepIndex,
}) => {
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const dropdownRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (visible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e) => {
      if (
        anchorRef.current &&
        !anchorRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible]);

  return (
    <>
      <div ref={anchorRef} className="relative inline-block">
        <Button
          ref={anchorRef}
          variant="ghost"
          className=" !px-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setVisible((v) => !v);
          }}
        >
          <div className="pointer-events-none absolute top-0 left-0 h-full w-full rounded-md bg-white opacity-10"></div>
          <MoreVertical className="h-4 w-4 text-white" />
        </Button>
      </div>

      {visible &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              top: position.top,
              left: position.left,
              zIndex: 9999,
              position: 'absolute',
              transform: 'translateX(calc(-100% + 45px))',
            }}
            className="absolute mt-2 min-w-60 divide-y divide-solid divide-grey-02 rounded-lg bg-white p-2 drop-shadow-m"
          >
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                onClick={(e) => {
                  setVisible(false);
                  addBefore(i);
                  newActive(
                    e,
                    i,
                    prevP?.end ? prevP?.end : p?.start > 2 ? p?.start - 2 : 0,
                    p?.start
                  );
                }}
                className="flex items-center gap-2 text-sm"
              >
                <ArrowLeft className="h-6 w-6" />
                {t(
                  'pages.create_edit_course.video_instruction.instruction_before'
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={(e) => {
                  setVisible(false);
                  addAfter(i);
                  newActive(
                    e,
                    i + 1,
                    p?.end,
                    nextP?.start ? nextP?.start : durationSec
                  );
                }}
                className="flex items-center gap-2 text-sm"
              >
                <ArrowRight className="h-6 w-6" />
                {t(
                  'pages.create_edit_course.video_instruction.instruction_after'
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={(e) => {
                  setVisible(false);
                  splitStep(i);
                  newActive(
                    e,
                    i + 1,
                    p?.end,
                    nextP?.start ? nextP?.start : durationSec
                  );
                }}
                className="flex items-center gap-2 text-sm"
              >
                <SplitIcon className="h-6 w-6" />
                {t(
                  'pages.create_edit_course.video_instruction.split_instruction'
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setVisible(false);
                  removeStep(i);
                  setActiveStepIndex(null);
                }}
                className="flex items-center gap-2 text-sm"
              >
                <img
                  src="/images/icons/trash_icon_red.svg"
                  alt="delete button"
                  className="w-5"
                />
                {t(
                  'pages.create_edit_course.video_instruction.delete_instruction'
                )}
              </Button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

const SegmentTimelineContent = ({
  durationSec,
  steps,
  currentTime,
  onSeek,
  onUpdateStep,
  onUpdateMany,
  setActiveStepIndex,
  msToMMSS,
  addAfter,
  addBefore,
  splitStep,
  removeStep,
  drag,
  setDrag,
  subtitles = [],
  activeSubtitleIndex,
  setActiveSubtitleIndex,
}) => {
  const safeDur = durationSec > 0 ? durationSec : 1;
  const containerRef = React.useRef(null);
  // const [drag, setDrag] = React.useState(null); // {mode:'move'|'left'|'right', idx, _origIndex, ...}

  // Build ordered view but keep original index for state updates
  const segments = useMemo(() => {
    const ordered = steps
      .map((s, i) => ({ ...s, _origIndex: i }))
      .sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0));
    return ordered.map((s, idx) => {
      const startSec = clamp((s.start_ms || 0) / 1000, 0, safeDur);
      const endSec = clamp((s.end_ms || 0) / 1000, startSec, safeDur);
      const title = s.title || `Step ${idx + 1}`;
      const active =
        (currentTime > startSec - EPS && currentTime < endSec - 1e-6) ||
        (idx === ordered.length - 1 && currentTime >= endSec - EPS);
      return {
        idx,
        _origIndex: s._origIndex,
        startSec,
        endSec,
        title,
        active,
        color: colorFor(idx),
      };
    });
  }, [steps, currentTime, safeDur]);

  const subtitleSegments = useMemo(() => {
    return (Array.isArray(subtitles) ? subtitles : [])
      .map((s, i) => {
        const startSec = clamp((s.start_ms || 0) / 1000, 0, safeDur);
        const endSec = clamp((s.end_ms || 0) / 1000, startSec, safeDur);
        const active =
          (currentTime > startSec - EPS && currentTime < endSec - 1e-6) ||
          (i === subtitles.length - 1 && currentTime >= endSec - EPS);

        return {
          idx: i,
          startSec,
          endSec,
          text: s.text || '',
          active,
        };
      })
      .sort((a, b) => a.startSec - b.startSec);
  }, [subtitles, currentTime, safeDur]);

  // Compute previewed start/end for each segment when dragging
  const getPreviewStartEnd = (seg) => {
    if (!drag) {
      return { start: seg?.startSec, end: seg?.endSec };
    }

    const baseStart = seg?.startSec;
    const baseEnd = seg?.endSec;
    const activeIdx = drag.idx;
    const mode = drag.mode;

    if (seg?.idx === activeIdx) {
      return {
        start: drag.previewStart ?? seg?.startSec,
        end: drag.previewEnd ?? seg?.endSec,
      };
    }

    if (mode === 'left' && seg?.idx === activeIdx - 1) {
      const boundary = drag.previewStart ?? drag.startSec;
      const originalEnd = baseEnd;

      if (boundary >= originalEnd) {
        return { start: baseStart, end: baseEnd };
      }

      const newEnd = Math.max(boundary, baseStart + MIN_SPAN);
      return { start: baseStart, end: newEnd };
    }

    if (mode === 'right' && seg?.idx === activeIdx + 1) {
      const boundary = drag.previewEnd ?? drag.endSec;
      const originalStart = baseStart;

      if (boundary <= originalStart) {
        return { start: baseStart, end: baseEnd };
      }

      const newStart = Math.min(boundary, baseEnd - MIN_SPAN);
      return { start: newStart, end: baseEnd };
    }

    return { start: baseStart, end: baseEnd };
  };

  // Bounds for dragging
  const boundsForMove = (idx, span) => {
    const prevEnd = idx > 0 ? segments[idx - 1].endSec : 0;
    const nextStart =
      idx < segments.length - 1 ? segments[idx + 1].startSec : safeDur;
    const left = prevEnd + 1e-6; // tiny pad
    const right = nextStart - span - 1e-6;
    return {
      left: clamp(left, 0, Math.max(0, safeDur - span)),
      right: clamp(right, 0, Math.max(0, safeDur - span)),
    };
  };

  const boundsForLeftEdge = (idx) => {
    // dragging the left edge of segment idx (affects previous end if not first)
    const cur = segments[idx];
    const prev = segments[idx - 1];
    // minimum/maximum for the shared boundary:
    const min = idx === 0 ? 0 : prev.startSec + MIN_SPAN;
    const max = cur.endSec - MIN_SPAN;
    return { min: clamp(min, 0, safeDur), max: clamp(max, 0, safeDur) };
  };

  const boundsForRightEdge = (idx) => {
    // dragging the right edge of segment idx (affects next start if not last)
    const cur = segments[idx];
    const next = segments[idx + 1];
    const min = cur.startSec + MIN_SPAN;
    const max = idx === segments.length - 1 ? safeDur : next.endSec - MIN_SPAN;
    return { min: clamp(min, 0, safeDur), max: clamp(max, 0, safeDur) };
  };

  // Pointer handlers
  const onPointerDown = (e, mode, seg) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const secPerPx = safeDur / rect.width;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({
      mode,
      idx: seg.idx,
      _origIndex: seg._origIndex,
      startSec: seg.startSec,
      endSec: seg.endSec,
      secPerPx,
      x0: e.clientX,
      y0: e.clientY,
      isDragging: false,
    });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const dxPx = e.clientX - drag.x0;
    const dyPx = e.clientY - drag.y0;
    const moved = Math.hypot(dxPx, dyPx) > CLICK_TOL;
    const dxSec = dxPx * drag.secPerPx;

    let start = drag.startSec;
    let end = drag.endSec;

    if (drag.mode === 'move') {
      const span = Math.max(MIN_SPAN, end - start);
      const { left, right } = boundsForMove(drag.idx, span);
      let ns = clamp(start + dxSec, left, right);
      start = ns;
      end = ns + span;
    } else if (drag.mode === 'left') {
      const { min, max } = boundsForLeftEdge(drag.idx);
      const newBoundary = clamp(drag.startSec + dxSec, min, max);
      start = newBoundary;
      // end unchanged; neighbor’s end will preview to this boundary in render
    } else if (drag.mode === 'right') {
      const { min, max } = boundsForRightEdge(drag.idx);
      const newBoundary = clamp(drag.endSec + dxSec, min, max);
      end = newBoundary;
      // start unchanged; neighbor’s start will preview to this boundary in render
    }

    // snap
    const snap = (x) => Math.round(x / SNAP) * SNAP;
    start = snap(start);
    end = snap(end);

    setDrag({
      ...drag,
      isDragging: moved,
      previewStart: start,
      previewEnd: end,
    });
  };

  const commitUpdates = (updates) => {
    if (onUpdateMany && Array.isArray(updates) && updates.length > 1) {
      onUpdateMany(updates);
      return;
    }
    // fallback: call single updater sequentially
    updates.forEach(({ _origIndex, patch }) => {
      onUpdateStep?.(_origIndex, patch.start_ms, patch.end_ms);
    });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const start = drag.previewStart ?? drag.startSec;
    const end = drag.previewEnd ?? drag.endSec;

    // Build update(s) depending on the mode and edge
    if (drag.mode === 'move') {
      commitUpdates([
        {
          _origIndex: drag._origIndex,
          patch: {
            start_ms: Math.round(start * 1000),
            end_ms: Math.round(end * 1000),
          },
        },
      ]);
      setDrag(null);
      return;
    }

    if (drag.mode === 'left') {
      const updates = [];
      updates.push({
        _origIndex: segments[drag.idx]._origIndex,
        patch: {
          start_ms: Math.round(start * 1000),
          end_ms: Math.round(segments[drag.idx].endSec * 1000),
        },
      });

      if (drag.idx > 0) {
        const prevSeg = segments[drag.idx - 1];
        if (start < prevSeg.endSec - MIN_SPAN) {
          updates.push({
            _origIndex: prevSeg._origIndex,
            patch: {
              start_ms: Math.round(prevSeg.startSec * 1000),
              end_ms: Math.round(start * 1000),
            },
          });
        }
      }

      commitUpdates(updates);
      setDrag(null);
      return;
    }

    if (drag.mode === 'right') {
      const updates = [];
      updates.push({
        _origIndex: segments[drag.idx]._origIndex,
        patch: {
          start_ms: Math.round(segments[drag.idx].startSec * 1000),
          end_ms: Math.round(end * 1000),
        },
      });

      if (drag.idx < segments.length - 1) {
        const nextSeg = segments[drag.idx + 1];
        if (end > nextSeg.startSec + MIN_SPAN) {
          updates.push({
            _origIndex: nextSeg._origIndex,
            patch: {
              start_ms: Math.round(end * 1000),
              end_ms: Math.round(nextSeg.endSec * 1000),
            },
          });
        }
      }

      commitUpdates(updates);
      setDrag(null);
      return;
    }
  };

  React.useEffect(() => {
    if (!drag) return;
    const move = (e) => onPointerMove(e);
    const up = () => onPointerUp();
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = prev;
    };
  }, [drag]);

  // Click to seek on empty bar
  const onBarClick = (e) => {
    if (drag) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = clamp(e.clientX - rect.left, 0, rect.width);
    const sec = (px / rect.width) * safeDur;
    onSeek?.(sec);
  };

  // Ruler ticks – limit to ~MAX_TICKS "nice" intervals
  const ticks = useMemo(() => {
    if (!Number.isFinite(safeDur) || safeDur <= 0) return [0];

    // target raw step size
    const rawStep = safeDur / MAX_TICKS;

    // choose a nice step: 1, 2, 5 × 10^k
    const pow10 = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const candidates = [1, 2, 5].map((m) => m * pow10);
    let interval = candidates[candidates.length - 1];
    for (const step of candidates) {
      if (rawStep <= step) {
        interval = step;
        break;
      }
    }

    const arr = [];
    for (let t = 0; t <= safeDur + 1e-6; t += interval) {
      arr.push(clamp(t, 0, safeDur));
    }
    // ensure last tick at exact duration
    if (arr[arr.length - 1] !== safeDur) arr.push(safeDur);

    // de-dup and trim precision
    const uniq = Array.from(new Set(arr.map((x) => +x.toFixed(3))));
    return uniq;
  }, [safeDur]);

  // --- Floating time indicator when dragging ---
  let dragTooltip = null;

  if (drag && drag.isDragging) {
    const seg = segments[drag.idx];
    const pStart = drag.previewStart ?? seg.startSec;
    const pEnd = drag.previewEnd ?? seg.endSec;

    // Which value to show: depends on mode
    let t = null;
    if (drag.mode === 'move') t = pStart;
    else if (drag.mode === 'left') t = pStart;
    else if (drag.mode === 'right') t = pEnd;

    if (t != null && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const leftPct = (t / safeDur) * 100;
      dragTooltip = (
        <div
          className="pointer-events-none absolute -top-5 select-none rounded-sm bg-black/80 px-1.5 py-[1px] text-[11px] font-semibold text-white"
          style={{
            left: `calc(${leftPct}% - 20px)`,
            minWidth: '40px',
            textAlign: 'center',
          }}
        >
          {fmt(t)}
        </div>
      );
    }
  }

  return (
    <>
      <div
        data-timeline-container
        style={{
          position: 'relative',
          width: `${steps.length * 300}px`,
          transform: 'translateX(0px)',
          transition: 'transform 0s',
          willChange: 'transform',
          minWidth: '100%',
        }}
      >
        {/* ruler */}
        <div className="flex justify-between px-2 text-xs text-grey-05">
          {ticks.map((t, i) => (
            <>
              {i != 0 && <span>•</span>}
              {i != 0 && <span>•</span>}
              <span key={i} className="">
                {fmt(t)}
              </span>
            </>
          ))}
        </div>

        {/* main bar */}
        <div
          ref={containerRef}
          className="relative mt-2 h-[42px] w-full cursor-pointer select-none bg-grey-02 outline-none focus:outline-none"
          style={{ caretColor: 'transparent', userSelect: 'none' }}
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()} // <— prevent focus/caret
          onClick={onBarClick}
        >
          {segments.map((seg, i) => {
            const p = getPreviewStartEnd(seg);
            const leftPct = (p.start / safeDur) * 100;
            const widthPct = ((p.end - p.start) / safeDur) * 100;
            const isSel = seg.active || (drag && drag.idx === seg.idx);
            const nextP = getPreviewStartEnd(segments[i + 1]);
            const prevP = getPreviewStartEnd(segments[i - 1]);

            return (
              <div
                key={seg.idx}
                className="absolute top-0 h-[42px] outline-none transition focus:outline-none"
                style={{
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  zIndex: 10,
                  caretColor: 'transparent',
                  userSelect: 'none',
                }}
                onMouseDown={(e) => e.preventDefault()} // <— prevent focus/caret here too
                onClick={(e) => {
                  e.stopPropagation();
                  onSeek?.(safeInside(p.start, p.end));
                  setActiveStepIndex(seg.idx + 1);
                }}
                title={seg.title}
              >
                {/* Colored fill, inset to create the grey gap AND extra padding from selection */}
                <div
                  className="rounded-[4px] border-y  border-x-8 border-grey-01"
                  style={{
                    position: 'absolute',
                    top: SELECT_PAD,
                    bottom: SELECT_PAD,
                    left: GAP_PX / 2 + SELECT_PAD,
                    right: GAP_PX / 2 + SELECT_PAD,
                    backgroundColor: seg.color,
                    pointerEvents: 'none', // keeps hit-testing on the wrapper
                    userSelect: 'none',
                    // --- selection outline on the fill itself ---
                    ...(isSel
                      ? {
                          borderColor: '#4D4D50',
                          // outline: `${BORDER_W}px solid ${seg.color}`,
                          // Keep the moat (SELECT_PAD) and let the outline intrude into the gap
                          // outlineOffset: SELECT_PAD + OUTLINE_ENCROACH,
                        }
                      : null),
                  }}
                />

                <span className="absolute left-[5px] top-[11px] h-5 w-[2px] rounded-lg bg-grey-02"></span>
                <span className="absolute right-[5px] top-[11px] h-5 w-[2px] rounded-lg bg-grey-02"></span>

                {/* Inline labels (number left, start–end right) */}
                <div
                  className="absolute max-w-[calc(100%_-_22px)] text-[11px] font-medium"
                  style={{
                    top: SELECT_PAD,
                    bottom: SELECT_PAD,
                    left: GAP_PX / 2 + SELECT_PAD,
                    right: GAP_PX / 2 + SELECT_PAD,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 4px',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    color: '#FFFFFF',
                    zIndex: 11,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <span className="pl-3 text-xs font-semibold">
                    {seg.idx + 1}
                  </span>
                  <div className="ml-4 mr-8 max-w-[100%] overflow-hidden text-ellipsis text-xs text-white opacity-70">
                    {msToMMSS(seg?.startSec * 1000)} –{' '}
                    {msToMMSS(seg?.endSec * 1000)} •{' '}
                    {msToMMSS((seg?.endSec - seg?.startSec) * 1000)}
                  </div>
                </div>

                <div className="absolute top-[5px] right-[12px] z-[12] max-w-[calc(100%_-_22px)] overflow-hidden">
                  <ClickDropdownWrapper
                    i={i}
                    addBefore={addBefore}
                    addAfter={addAfter}
                    splitStep={splitStep}
                    removeStep={removeStep}
                    p={p}
                    prevP={prevP}
                    nextP={nextP}
                    durationSec={durationSec}
                    setActiveStepIndex={setActiveStepIndex}
                    newActive={(e, index, startS, endS) => {
                      e.stopPropagation();
                      onSeek?.(safeInside(startS, endS));
                      setActiveStepIndex(index + 1);
                    }}
                  />
                </div>

                {/* MOVE: body is the grab area (invisible) */}
                <div
                  className="absolute inset-0 cursor-grab active:cursor-grabbing"
                  style={{ userSelect: 'none', caretColor: 'transparent' }}
                  onPointerDown={(e) => onPointerDown(e, 'move', seg)}
                />

                {/* LEFT edge handle (invisible, comfy hit area) */}
                <div
                  className="absolute left-0 top-0 h-full cursor-ew-resize"
                  style={{
                    width: HANDLE_W,
                    userSelect: 'none',
                    caretColor: 'transparent',
                  }}
                  onPointerDown={(e) => onPointerDown(e, 'left', seg)}
                  title={
                    seg.idx === 0 ? 'Adjust start' : 'Adjust shared boundary'
                  }
                />

                {/* RIGHT edge handle (invisible, comfy hit area) */}
                <div
                  className="absolute right-0 top-0 h-full cursor-ew-resize"
                  style={{
                    width: HANDLE_W,
                    userSelect: 'none',
                    caretColor: 'transparent',
                  }}
                  onPointerDown={(e) => onPointerDown(e, 'right', seg)}
                  title={
                    seg.idx === segments.length - 1
                      ? 'Adjust end'
                      : 'Adjust shared boundary'
                  }
                />
              </div>
            );
          })}

          {dragTooltip}

          {/* playhead */}
          <div
            className="absolute top-[-38px] z-[10] h-[123px] w-[2px] bg-primary-04"
            style={{
              left: `${(clamp(currentTime, 0, safeDur) / safeDur) * 100}%`,
            }}
          >
            <span
              className="top absolute -left-1"
              style={{
                width: '0',
                height: '0',
                borderStyle: 'solid',
                borderWidth: '10px 5px 0 5px',
                borderColor: '#FDCA33 transparent transparent transparent',
                transform: 'rotate(0deg)',
              }}
            ></span>
            <span
              className=" absolute -left-1"
              style={{
                bottom: '0',
                width: '0',
                height: '0',
                borderStyle: 'solid',
                borderWidth: '0px 5px 10px 5px',
                borderColor: 'transparent transparent #FDCA33 transparent',
                transform: 'rotate(0deg)',
              }}
            ></span>
            <span className="absolute top-[43px] -left-[3px] h-8 w-2 rounded-lg bg-primary-04"></span>
          </div>
        </div>

        <div className="relative mt-2 h-8 w-full text-xs">
          {segments.map((seg) => {
            const p = getPreviewStartEnd(seg);
            const leftPct = (p.start / safeDur) * 100;
            const widthPct = ((p.end - p.start) / safeDur) * 100;

            return (
              <button
                key={`lbl-${seg.idx}`}
                type="button"
                className="absolute flex flex-row items-center gap-2 overflow-hidden text-ellipsis px-2 py-0.5 text-grey-09"
                style={{
                  color: seg.active ? '#2563eb' : 'inherit',
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSeek(safeInside(seg.startSec, seg.endSec));
                  setActiveStepIndex(seg.idx + 1);
                }}
                title={seg.title}
              >
                <span
                  className="inline-block h-4 w-4 min-w-4 rounded-[4px]"
                  style={{ backgroundColor: seg.color }}
                />
                <p
                  className="overflow-hidden text-ellipsis text-xs font-medium"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {seg.idx + 1} - {abbr(seg.title, 28)}
                </p>
              </button>
            );
          })}
        </div>

        {subtitleSegments.length > 0 && (
          <div className="relative mt-3 h-10 w-full rounded-md bg-white/60 px-1 py-1 shadow-inner">
            {subtitleSegments.map((seg) => {
              const leftPct = (seg.startSec / safeDur) * 100;
              const widthPct = ((seg.endSec - seg.startSec) / safeDur) * 100;

              return (
                <button
                  key={`sub-${seg.idx}`}
                  type="button"
                  className={`absolute top-1 h-8 overflow-hidden rounded-md px-2 text-left text-xs transition ${
                    seg.active
                      ? 'border border-secondary-04 bg-grey-06 text-white'
                      : 'bg-grey-03 text-grey-09'
                  }`}
                  style={{
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSeek?.(safeInside(seg.startSec, seg.endSec));
                    setActiveSubtitleIndex?.(seg.idx + 1);
                  }}
                  title={seg.text}
                >
                  <span className="block truncate">{seg.text}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export function SegmentTimeline({
  durationSec,
  steps,
  currentTime,
  onSeek,
  onUpdateStep,
  onUpdateMany,
  activeStepIndex,
  setActiveStepIndex,
  msToMMSS,
  addAfter,
  addBefore,
  splitStep,
  removeStep,
  isPlaying,
  subtitles = [],
  activeSubtitleIndex,
  setActiveSubtitleIndex,
}) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  const THUMB_SIZE = 32;

  const [thumbLeft, setThumbLeft] = useState(0);
  const [drag, setDrag] = useState(null);

  const syncFromScroll = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const ratio =
      viewport.scrollLeft / (viewport.scrollWidth - viewport.clientWidth);

    const trackW = trackRef.current.clientWidth;
    const maxLeft = trackW - THUMB_SIZE;

    setThumbLeft(ratio * maxLeft);
  };

  const onThumbDrag = (e) => {
    const track = trackRef.current;
    const viewport = viewportRef.current;
    const rect = track.getBoundingClientRect();

    let pos = e.clientX - rect.left - THUMB_SIZE / 2;
    pos = Math.max(0, Math.min(pos, rect.width - THUMB_SIZE));

    const ratio = pos / (rect.width - THUMB_SIZE);
    viewport.scrollLeft = ratio * (viewport.scrollWidth - viewport.clientWidth);
  };

  const startDrag = () => {
    document.addEventListener('mousemove', onThumbDrag);
    document.addEventListener('mouseup', stopDrag);
  };

  const stopDrag = () => {
    document.removeEventListener('mousemove', onThumbDrag);
    document.removeEventListener('mouseup', stopDrag);
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    viewport.addEventListener('scroll', syncFromScroll);
    window.addEventListener('resize', syncFromScroll);

    return () => {
      viewport.removeEventListener('scroll', syncFromScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, []);
  useEffect(() => {
    if (!isPlaying) return;
    if (drag) return;

    const viewport = viewportRef.current;
    const container = viewport?.querySelector('[data-timeline-container]');
    if (!viewport || !container) return;

    const safeDur = durationSec > 0 ? durationSec : 1;
    const ratio = clamp(currentTime, 0, safeDur) / safeDur;
    const playheadX = ratio * container.scrollWidth;

    const left = viewport.scrollLeft;
    const right = left + viewport.clientWidth;
    const PADDING = 40;

    if (playheadX < left + PADDING || playheadX > right - PADDING) {
      viewport.scrollTo({
        left: playheadX - viewport.clientWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [currentTime, durationSec, isPlaying, drag]);

  useEffect(() => {
    if (!isPlaying) return;
    if (drag) return;
    if (activeStepIndex == null) return;

    const viewport = viewportRef.current;
    const container = viewport?.querySelector('[data-timeline-container]');
    if (!viewport || !container) return;

    const safeDur = durationSec > 0 ? durationSec : 1;
    const seg = steps[activeStepIndex - 1];
    if (!seg) return;

    const startSec = clamp(seg.start_ms / 1000, 0, safeDur);
    const ratio = startSec / safeDur;
    const targetX = ratio * container.scrollWidth;

    viewport.scrollTo({
      left: targetX - viewport.clientWidth / 2,
      behavior: 'smooth',
    });
  }, [activeStepIndex, drag]);

  return (
    <div className="relative w-full select-none overflow-hidden rounded-lg bg-grey-01 pt-4">
      <div
        ref={trackRef}
        className="relative mt-2 mb-4 h-[1px] rounded bg-gray-300"
      >
        <div
          className=" absolute top-1/2 flex -translate-y-1/2 cursor-grab flex-row items-center rounded bg-yellow-400 shadow active:cursor-grabbing"
          style={{
            width: THUMB_SIZE + 'px',
            height: THUMB_SIZE + 'px',
            transform: `translate(${thumbLeft}px, -50%)`,
          }}
          onMouseDown={startDrag}
        >
          <ChevronLeft className="h-5 w-5" />
          <ChevronRight className="h-5 w-5" />
        </div>
      </div>
      <div
        ref={viewportRef}
        className="overflow-x-scroll"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '16px 0 36px',
        }}
      >
        <style>{`
          .overflow-x-scroll::-webkit-scrollbar { display: none; }
        `}</style>

        <SegmentTimelineContent
          durationSec={durationSec}
          steps={steps}
          currentTime={currentTime}
          onSeek={onSeek}
          onUpdateStep={onUpdateStep}
          onUpdateMany={onUpdateMany}
          setActiveStepIndex={setActiveStepIndex}
          msToMMSS={msToMMSS}
          addAfter={addAfter}
          addBefore={addBefore}
          splitStep={splitStep}
          removeStep={removeStep}
          drag={drag}
          setDrag={setDrag}
          subtitles={subtitles}
          activeSubtitleIndex={activeSubtitleIndex}
          setActiveSubtitleIndex={setActiveSubtitleIndex}
        />
      </div>
    </div>
  );
}
